import React, { useRef, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Paper, Button } from '@mui/material';
import { RiArrowGoBackLine, RiArrowGoForwardLine } from '@remixicon/react';
import {
  selectElement,
  deselectElement,
  updateElementPosition,
  updateElementSize,
  updateElementProperties,
  undo,
  redo,
  saveToHistory,
} from '../../redux/slices/canvasSlice';
import CanvasElementV2 from './CanvasElementV2';

/**
 * Canvas Board V2 - Main editing canvas
 * 
 * Renders the artboard with all elements
 * Supports drag, drop, resize, selection
 */
const CanvasBoardV2 = () => {
  const dispatch = useDispatch();
  const canvasRef = useRef(null);
  const [scale, setScale] = useState(1);
  
  const {
    orientation,
    dimensions,
    elements,
    selectedElementId,
    background,
    zoom,
    past,
    future,
  } = useSelector((state) => state.canvas);
  
  const canvasDimensions = dimensions[orientation];
  
  const canUndo = past.length > 0;
  const canRedo = future.length > 0;
  
  // Set scale based on zoom only (no auto-scaling to fit)
  useEffect(() => {
    setScale(zoom);
  }, [zoom]);
  
  const handleUndo = () => {
    dispatch(undo());
  };
  
  const handleRedo = () => {
    dispatch(redo());
  };
  
  const handleCanvasClick = (e) => {
    if (e.target === canvasRef.current || e.target.id === 'canvas-background') {
      dispatch(deselectElement());
    }
  };
  
  const handleElementClick = (elementId, e) => {
    e.stopPropagation();
    dispatch(selectElement(elementId));
  };
  
  const handleElementDragStart = useRef(false);
  const handleElementResizeStart = useRef(false);
  
  const handleElementDrag = (elementId, newPosition, isStart) => {
    if (isStart && !handleElementDragStart.current) {
      dispatch(saveToHistory());
      handleElementDragStart.current = true;
    }
    dispatch(updateElementPosition({ id: elementId, position: newPosition }));
  };
  
  const handleElementDragEnd = () => {
    handleElementDragStart.current = false;
  };
  
  const handleElementResize = (elementId, newSize, isStart) => {
    if (isStart && !handleElementResizeStart.current) {
      dispatch(saveToHistory());
      handleElementResizeStart.current = true;
    }
    dispatch(updateElementSize({ id: elementId, size: newSize }));
  };
  
  const handleElementResizeEnd = () => {
    handleElementResizeStart.current = false;
  };

  const handleTextUpdate = (elementId, newText) => {
    dispatch(saveToHistory());
    dispatch(updateElementProperties({
      id: elementId,
      properties: { text: newText },
    }));
  };
  
  const getBackgroundStyle = () => {
    console.log('getBackgroundStyle called with:', background);
    
    switch (background.type) {
      case 'color':
        if (background.value === 'transparent') {
          // Show white background when transparent (hidden)
          return { backgroundColor: '#FFFFFF' };
        }
        // If value is already rgba, use it directly; otherwise apply opacity
        if (background.value.startsWith('rgba')) {
          return { backgroundColor: background.value };
        }
        // Apply opacity if less than 100%
        if (background.opacity !== undefined && background.opacity < 100) {
          const hex = background.value.replace('#', '');
          const r = parseInt(hex.slice(0, 2), 16);
          const g = parseInt(hex.slice(2, 4), 16);
          const b = parseInt(hex.slice(4, 6), 16);
          return { backgroundColor: `rgba(${r}, ${g}, ${b}, ${background.opacity / 100})` };
        }
        return { backgroundColor: background.value };
      case 'image':
        const imageStyle = {
          backgroundImage: `url("${background.value}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        };
        console.log('Image background style:', imageStyle);
        // Apply opacity to image if less than 100%
        if (background.opacity !== undefined && background.opacity < 100) {
          imageStyle.opacity = background.opacity / 100;
        }
        return imageStyle;
      case 'video':
        console.log('Video background:', background.value);
        return {};
      default:
        return { backgroundColor: '#FFFFFF' };
    }
  };
  
  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Undo: Ctrl+Z (Windows/Linux) or Cmd+Z (Mac)
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        if (canUndo) {
          handleUndo();
        }
      }
      
      // Redo: Ctrl+Y (Windows/Linux) or Cmd+Shift+Z (Mac)
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.shiftKey && e.key === 'z'))) {
        e.preventDefault();
        if (canRedo) {
          handleRedo();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canUndo, canRedo]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        marginTop: 2,
      }}
    >
      {/* Undo/Redo Controls - Above Canvas */}
      <Box
        sx={{
          display: 'flex',
          backgroundColor: '#ffffff',
          borderRadius: '8px',
          padding: '4px',
          gap: 0,
          marginBottom: 1,
          width: 170,
        }}
      >
        <Button
          onClick={handleUndo}
          disabled={!canUndo}
          startIcon={<RiArrowGoBackLine size={24} />}
          sx={{
            textTransform: 'none',
            color: '#666666',
            fontSize: '14px',
            fontWeight: 400,
            padding: '6px 12px',
            minWidth: 'auto',
            borderRadius: '6px',
            backgroundColor: 'transparent',
            border: 'none',
            '&:hover': {
              backgroundColor: '#ffffff',
            },
            '&:disabled': {
              opacity: 0.4,
              cursor: 'not-allowed',
              color: '#666666',
            },
          }}
          title="Undo (Ctrl+Z)"
        >
          Undo
        </Button>
        <Button
          onClick={handleRedo}
          disabled={!canRedo}
          startIcon={<RiArrowGoForwardLine size={24} />}
          sx={{
            textTransform: 'none',
            color: '#666666',
            fontSize: '14px',
            fontWeight: 400,
            padding: '6px 12px',
            minWidth: 'auto',
            borderRadius: '6px',
            backgroundColor: 'transparent',
            border: 'none',
            '&:hover': {
              backgroundColor: '#ffffff',
            },
            '&:disabled': {
              opacity: 0.4,
              cursor: 'not-allowed',
              color: '#666666',
            },
          }}
          title="Redo (Ctrl+Y)"
        >
          Redo
        </Button>
      </Box>
      
      {/* Canvas Container */}
      <Box
        sx={{
          display: 'inline-block',
          minWidth: `${canvasDimensions.width * scale}px`,
          minHeight: `${canvasDimensions.height * scale}px`,
          position: 'relative',
        }}
      >

      <Paper
        ref={canvasRef}
        id="canvas-background"
        onClick={handleCanvasClick}
        // elevation={3}
        key={`${orientation}-${canvasDimensions.width}-${canvasDimensions.height}`}
        sx={{
          position: 'relative',
          width: `${canvasDimensions.width}px`,
          height: `${canvasDimensions.height}px`,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          cursor: 'default',
          overflow: 'hidden',
          ...getBackgroundStyle(),
        }}
      >
      {/* Background Video */}
      {background.type === 'video' && (
        <video
          src={background.value}
          autoPlay
          loop
          muted
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
            pointerEvents: 'none',
          }}
        />
      )}
      

      {/* Canvas Elements */}
      {elements.map((element) => (
        <CanvasElementV2
          key={element.id}
          element={element}
          allElements={elements}
          isSelected={element.id === selectedElementId}
          onClick={(e) => handleElementClick(element.id, e)}
          onDrag={(newPosition, isStart) => handleElementDrag(element.id, newPosition, isStart)}
          onDragEnd={handleElementDragEnd}
          onResize={(newSize, isStart) => handleElementResize(element.id, newSize, isStart)}
          onResizeEnd={handleElementResizeEnd}
          onTextUpdate={(newText) => handleTextUpdate(element.id, newText)}
          scale={scale}
        />
      ))}
      </Paper>
      </Box>
    </Box>
  );
};

export default CanvasBoardV2;

